import {
  DateField,
  dayjs,
  MenuItem,
  Select,
  Stack,
  TextField,
  useBirthDateValidator,
  useFirstNameValidator,
  useLastNameValidator,
  useSelectValidator,
} from '@ace-clublabs/shared-component-library';
import { useEffect, useState } from 'react';
import { useGlobalState } from '../../state/state';
import { getGenderList } from '../../state/helpers';
import { RoleType } from '@ace-clublabs/common-membership-api-types';
import { JuniorAssociateAlert } from '../JuniorAssociateAlert/JuniorAssociateAlert';
import { ClubCode } from '@lib/types';
import { IEditAssociateData } from '../../containers/AssociateForm';

export interface IAssociateInfoProps {
  mockGenderValue?: string;
  roleType: RoleType;
  loading?: boolean;
  editMode?: boolean;
  associate?: IEditAssociateData;
}

export const AssociateInfo = (props: IAssociateInfoProps) => {
  const [membershipDetails] = useGlobalState('memberDetails');
  const { clubCode } = membershipDetails;
  const genderList = getGenderList(membershipDetails);
  const [showAgeWarningBanner, setShowAgeWarningBanner] = useState(false);
  const { associate, editMode } = props;
  const roles = membershipDetails?.prospectiveMemberRules?.memberRoles;
  const role = roles.find((r) => r.roleType === (editMode ? associate?.memberRole : props.roleType));

  const getInitialAssociateState = () => {
    if (editMode) {
      return {
        firstName: associate?.firstName || '',
        lastName: associate?.lastName || '',
        dateOfBirth: associate?.dateOfBirth || '',
        gender: associate?.gender || '',
        roleType: associate?.memberRole || '',
        middleName: '',
        nameSuffix: '',
      };
    } else {
      return {
        firstName: '',
        middleName: '',
        lastName: '',
        nameSuffix: '',
        gender: props?.mockGenderValue || '', // for testing purposes only
        dateOfBirth: '',
      };
    }
  };

  const [associateFormData, setAssociateFormData] = useState(getInitialAssociateState());

  const firstNameValidator = useFirstNameValidator({
    maxLength: 20,
    validationFeedback: 'immediate',
    filterCharacterPattern: "[^a-z' -]",
    trim: false,
    pattern: "^(?=.*[A-Za-z])([A-Za-z'-]*[\\s]?[A-Za-z'-]*)$",
  });

  const lastNameValidator = useLastNameValidator({
    maxLength: 20,
    validationFeedback: 'immediate',
    filterCharacterPattern: "[^a-z' -]",
    trim: false,
    pattern: "^(?=.*[A-Za-z])([A-Za-z'-]*[\\s]?[A-Za-z'-]*)$",
  });

  const birthdayValidator = useBirthDateValidator({
    minAge: role?.minimumAge,
    maxAge: role?.maximumAge,
    required: true,
  });

  const disabledButtonStyles = {
    '& .Mui-disabled': {
      WebkitTextFillColor: '#707070 !important',
      '.MuiTextFieldHelperMessage': {
        WebkitTextFillColor: '#D0D0D0 !important',
      },
    },
  };

  const genderValidator = useSelectValidator({ validationFeedback: 'immediate' });

  function calculateAge(birthDateString: string): number {
    const birthDate = new Date(birthDateString);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();

    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }

  useEffect(() => {
    const isFullDOBEntered =
      typeof associateFormData.dateOfBirth === 'string' &&
      associateFormData.dateOfBirth.length === 10 &&
      String(Number(associateFormData.dateOfBirth.slice(6))).length === 4;

    if (isFullDOBEntered) {
      //when age input between 16 to 18, show banner
      const associateAge = calculateAge(associateFormData.dateOfBirth);
      if (associateAge >= 16 && associateAge <= 18) {
        setShowAgeWarningBanner(true);
      } else setShowAgeWarningBanner(false);
    }
  }, [associateFormData.dateOfBirth]);

  const renderECJuniorBanner =
    showAgeWarningBanner &&
    clubCode === ClubCode.EC &&
    (editMode ? associate?.memberRole : props.roleType) === RoleType.DEPENDENT;

  return (
    <div className="associate-info-section">
      <Stack direction={'column'} gap={'1rem'}>
        {renderECJuniorBanner && <JuniorAssociateAlert />}
        <TextField
          name="firstName"
          aria-label="firstName"
          fullWidth
          value={associateFormData.firstName}
          label="First name"
          id="firstName"
          validator={firstNameValidator}
          onChange={(v) => setAssociateFormData({ ...associateFormData, firstName: v.currentTarget.value })}
          onClearButtonClick={() => {
            setAssociateFormData({ ...associateFormData, firstName: '' });
          }}
          disabled={props.loading}
          sx={{
            '& .Mui-disabled .MuiInputBase-input': {
              ...disabledButtonStyles['& .Mui-disabled'],
            },
          }}
          data-qm-block="true"
        />
        <TextField
          name="lastName"
          aria-label="lastName"
          fullWidth
          value={associateFormData.lastName}
          label="Last name"
          id="lastName"
          required
          validator={lastNameValidator}
          onChange={(v) => setAssociateFormData({ ...associateFormData, lastName: v.currentTarget.value })}
          onClearButtonClick={() => {
            setAssociateFormData({ ...associateFormData, lastName: '' });
          }}
          disabled={props.loading}
          sx={disabledButtonStyles}
          data-qm-block="true"
        />
        <DateField
          aria-label="dateOfBirth"
          name="dateOfBirth"
          label="Date of birth"
          id="associateDOB"
          fullWidth
          helperText={'mm/dd/yyyy'}
          onClearButtonClick={() => setAssociateFormData({ ...associateFormData, dateOfBirth: '' })}
          validator={birthdayValidator}
          value={associateFormData.dateOfBirth === '' ? null : dayjs(associateFormData.dateOfBirth)}
          onChange={(v) => setAssociateFormData({ ...associateFormData, dateOfBirth: v?.format('MM/DD/YYYY') ?? '' })}
          disabled={props.loading}
          sx={disabledButtonStyles}
          data-qm-block="true"
        />
        <Select
          label="Gender"
          name="gender"
          id="gender"
          placeholder="Select one"
          fullWidth
          helperText="Must match driver's license or ID."
          value={genderList.find((g) => {
            return g.genderValue === (associateFormData.gender ?? '');
          })}
          getDisplayText={(v: any) => {
            return v.genderName;
          }}
          validator={genderValidator}
          onChange={(v: any) => {
            const x = v.target.value;
            setAssociateFormData({ ...associateFormData, gender: x.genderValue });
          }}
          sx={disabledButtonStyles}
          disabled={props.loading}
          data-qm-block="true"
        >
          {genderList.map((gender, i) => (
            <MenuItem key={gender.genderName} value={gender as any} id={gender.genderValue}>
              {gender.genderName}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    </div>
  );
};
